import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { DynamicOverlayService, OVERLAY_DATA } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { StorageService } from 'src/app/storage.service';
import { ContentStateService } from 'src/app/views/content/content-state.service';
import { ChunkService } from '../../chunk.service';
import { CanvasChunkDialogComponent } from '../canvas-chunk-dialog/canvas-chunk-dialog.component';

@Component({
  selector: 'app-add-canvas-option',
  standalone: true,
  imports: [TranslateModule, MatIconModule, MatDialogModule],
  template: `
    <div class="interactive-item" (click)="onClick()">
      <div class="interactive-item__content">
        <mat-icon class="interactive-item__icon" aria-hidden="true">wysiwyg</mat-icon>
        <span class="interactive-item__text">{{ 'Createhunk.AddCanvas' | translate }}</span>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddCanvasOptionComponent {
  @Input() chunk: any;
  @Output() actionTriggered = new EventEmitter<void>();

  private dialog = inject(MatDialog);
  private overlay = inject(DynamicOverlayService);
  private chunkService = inject(ChunkService);
  private contentStateService = inject(ContentStateService);
  private storageService = inject(StorageService);
  private translate = inject(TranslateService);
  private data: { contentID: string; courseID?: string; blogID?: string; chunkAboveId?: string; chunkBelowId?: string } = inject(OVERLAY_DATA);

  async onClick(): Promise<void> {
    const dialogRef = this.dialog.open(CanvasChunkDialogComponent, {
      width: '90vw',
      maxWidth: '600px',
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (!result) return;

    const { title } = result;
    const { contentID, courseID, blogID, chunkAboveId, chunkBelowId } = this.data;

    const chunkParams = {
      contentID,
      courseID,
      blogID,
      type: 'canvas-transcript',
      title,
      content: '<p></p>',
      language: this.translate.currentLang,
    };

    const serverResponse = await firstValueFrom(
      this.chunkService.createChunk({
        chunks: [chunkParams],
        insertBelowID: chunkAboveId,
        insertAboveID: chunkBelowId,
      })
    );

    const finalChunks = serverResponse.map((res) => ({ ...res.chunk, status: 'saved' }));
    await this.storageService.bulkStore('chunks', finalChunks);
    await this.contentStateService.getPieces(true);

    const createdChunk = finalChunks[0];
    const mainEdition = createdChunk.editions?.find((ed: any) => ed._id === createdChunk.main) || createdChunk.editions?.[0];

    if (createdChunk && mainEdition) {
      this.contentStateService.openCanvas({
        contentId: contentID,
        chunkId: createdChunk._id,
        title: createdChunk.title || title,
        html: mainEdition.content || '',
        edition: mainEdition,
        source: 'transcript',
      });
    }

    this.actionTriggered.emit();
    this.overlay.close();
  }
}
